import type { LayerKey } from "../api/types";
import { BRIDGE_IMAGE } from "./bridgeImages";

export const BRIDGE_LAYER_ID = "bridges-symbol";

/** Styles des couches du camp. Écrits dans le sous-ensemble commun à Mapbox GL JS et MapLibre GL JS
 *  (style JSON v8), pour que le client reste indépendant du moteur. */

type Paint = Record<string, unknown>;
type Layout = Record<string, unknown>;

export interface LayerStyle {
  id: string;
  type: "fill" | "line" | "circle" | "symbol";
  paint?: Paint;
  layout?: Layout;
  minzoom?: number;
  filter?: unknown[];
}

const ROAD_COLOR = [
  "match",
  ["get", "class"],
  ["motorway", "motorway_link", "trunk", "trunk_link"],
  "#d9822b",
  ["primary", "primary_link"],
  "#e8a65a",
  ["secondary", "secondary_link"],
  "#cdb46f",
  "#b9b9b9",
];

const ROAD_WIDTH = [
  "interpolate",
  ["linear"],
  ["zoom"],
  9,
  ["match", ["get", "class"], ["motorway", "trunk"], 1.6, ["primary"], 1.1, 0.5],
  14,
  ["match", ["get", "class"], ["motorway", "trunk"], 5, ["primary"], 3.5, ["secondary"], 2.5, 1.6],
];

export const LAYER_STYLES: Record<LayerKey, LayerStyle[]> = {
  boundary: [
    // Limite du camp en gris ardoise : sur une carte tactique, le rouge désigne l'ennemi.
    { id: "boundary-fill", type: "fill", paint: { "fill-color": "#334155", "fill-opacity": 0.03 } },
    {
      id: "boundary-line",
      type: "line",
      paint: { "line-color": "#334155", "line-width": 1.6, "line-dasharray": [3, 2], "line-opacity": 0.8 },
    },
  ],
  forest: [
    { id: "forest-fill", type: "fill", paint: { "fill-color": "#8cc38a", "fill-opacity": 0.45 } },
    { id: "forest-line", type: "line", minzoom: 11, paint: { "line-color": "#5f9d5c", "line-width": 0.7 } },
  ],
  water: [{ id: "water-fill", type: "fill", paint: { "fill-color": "#9cc9ef", "fill-opacity": 0.75 } }],
  urban: [{ id: "urban-fill", type: "fill", paint: { "fill-color": "#b7b0a6", "fill-opacity": 0.4 } }],
  rivers: [{ id: "rivers-line", type: "line", paint: { "line-color": "#4d93d8", "line-width": 1.4 } }],
  roads: [
    {
      id: "roads-line",
      type: "line",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": ROAD_COLOR, "line-width": ROAD_WIDTH },
    },
  ],
  bridges: [
    {
      // Symbole OTAN du pont, tourné le long de la route qui le franchit (`rotation`, calculée par le serveur) ;
      // l'image (intact ou détruit) est choisie par la carte selon l'état du dossier (`bridgeImageExpression`).
      id: BRIDGE_LAYER_ID,
      type: "symbol",
      layout: {
        "icon-image": BRIDGE_IMAGE,
        "icon-rotate": ["get", "rotation"],
        "icon-rotation-alignment": "map",
        "icon-size": ["interpolate", ["linear"], ["zoom"], 9, 0.75, 13, 1],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
    },
  ],
  chokepoints: [
    {
      id: "chokepoints-circle",
      type: "circle",
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 2.5, 14, 5],
        // Vert des obstacles (APP-6) foncé à clair selon la sévérité : le rouge est celui de l'ennemi.
        "circle-color": ["match", ["get", "severity"], "HIGH", "#006B24", "MEDIUM", "#1E9C45", "#6CC287"],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1,
      },
    },
  ],
  grid: [
    {
      id: "grid-circle",
      type: "circle",
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 1.5, 13, 3],
        "circle-color": ["case", ["get", "valid"], "#4b5160", "#d9480f"],
        "circle-opacity": 0.7,
      },
    },
    {
      id: "grid-label",
      type: "symbol",
      minzoom: 11,
      layout: {
        "text-field": ["to-string", ["get", "square"]],
        "text-size": 10,
        "text-offset": [0, 0.9],
        "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
      },
      paint: { "text-color": "#4b5160", "text-halo-color": "#ffffff", "text-halo-width": 1.2 },
    },
  ],
};

/** Ordre de superposition : du fond vers le dessus. */
export const LAYER_ORDER: LayerKey[] = [
  "urban",
  "forest",
  "water",
  "boundary",
  "rivers",
  "roads",
  "chokepoints",
  "bridges",
  "grid",
];

/** Couches interrogeables au clic, pour la fiche d'objet. */
export const INTERACTIVE_LAYER_IDS = [BRIDGE_LAYER_ID, "chokepoints-circle", "grid-circle", "roads-line"];

export function layerKeyOf(styleId: string): LayerKey | null {
  for (const key of LAYER_ORDER) {
    if (LAYER_STYLES[key].some((s) => s.id === styleId)) return key;
  }
  return null;
}
